import { ShadowMapping, ShadowPlane } from '@easterngraphics/wcf/modules/core/rendering';
import * as core from '@easterngraphics/wcf/modules/core';
import * as tool from '@easterngraphics/wcf/modules/core/tool';
import * as cf from '@easterngraphics/wcf/modules/cf';
import * as basket from '@easterngraphics/wcf/modules/eaiws/basket';
import * as eaiws from '@easterngraphics/wcf/modules/eaiws';
import { ajax } from '@easterngraphics/wcf/modules/utils/async';
import { isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';
import { Log } from '@easterngraphics/wcf/modules/utils';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
import './index.css';
import { CameraControl } from '@easterngraphics/wcf/modules/core/view/CameraControl';
import constants, { getAssetUrl } from '../../helpers/constants';
import { translateLocalStorage } from '../../helpers/i18n';
import { buildAssetUrl, buildPdfTemplateUrl, runtimeConfig } from '../../helpers/runtime-config';
import { HtmlUtils } from '../../utils';
import { ProgressUI } from '../progress';
/**
 * This components creates the views and the controls of the viewer.
 */

export interface ViewerOptions {
    disableCameraPanning: boolean;
    limitCameraDistanceByElementRadius: boolean;
}
export class ViewerUI {
    private coreApp: core.Application;
    private options: ViewerOptions;
    private session: eaiws.EaiwsSession;
    private articleManager: cf.ArticleManager;
    private canvasWidth = 0;
    private readonly resizeListener: () => void;

    /**
     * @param multiView If true, four views will be created instead of one.
     */
    constructor(coreApp: core.Application, options: ViewerOptions, session: eaiws.EaiwsSession, articleManager: cf.ArticleManager) {
        this.coreApp = coreApp;
        this.options = options;
        this.session = session;
        this.articleManager = articleManager;
        this.resizeListener = this.setCanvas.bind(this);
        if (this.options.disableCameraPanning) {
            const cameraControl = coreApp.viewer.view.cameraControl;
            cameraControl.panningEnabled = false;
            cameraControl.setFixedTarget(Vector3.Zero());
            cameraControl.dblClickZoomToFitOptions.adjustFixedTarget = false;
        }
        const defaultTool: tool.SelectionDefault = new tool.SelectionDefault(this.coreApp);
        defaultTool.deselectionEnabled = false;
        defaultTool.showMainElementSelection = false;
        defaultTool.showElementInteractors = true;
        this.coreApp.tools.defaultTool = defaultTool;
        this.coreApp.tools.startDefaultTool();

        this.coreApp.rendering.addShadowGenerator(new ShadowPlane(this.coreApp));
        this.coreApp.rendering.addShadowGenerator(new ShadowMapping(this.coreApp));

        const container = HtmlUtils.getNotNullElementById('pcon-viewer') as HTMLDivElement;

        const topLeftControlContainer: HTMLDivElement = document.createElement('div');
        topLeftControlContainer.className = 'pcon-viewer-control-tl';

        const topRightControlContainer: HTMLDivElement = document.createElement('div');
        topRightControlContainer.className = 'pcon-viewer-control-tr';

        const zoomInButton = this.createButton('zoom-in', 'icon_edit_plus.svg', translateLocalStorage('zoomIn'), this.zoomIn.bind(this));
        const zoomOutButton = this.createButton('zoom-out', 'icon_edit_minus.svg', translateLocalStorage('zoomOut'), this.zoomOut.bind(this));
        const fullscreenButton = this.createButton('fullscreen', 'icon_edit_move.svg', translateLocalStorage('fullscreen'), this.fullscreen.bind(this));

        topRightControlContainer.append(zoomInButton, zoomOutButton, fullscreenButton);
        container.append(topLeftControlContainer, topRightControlContainer);

        const screenshotButton = this.createButton('camera', 'icon_snapshot.svg', translateLocalStorage('snapshot'), this.screenShot.bind(this));
        container.appendChild(screenshotButton);

        const exportDropDown = this.createExportDropdown();
        exportDropDown.className = 'dnt-dropdown-export';
        exportDropDown.title = translateLocalStorage('exportPlanningData');
        exportDropDown.addEventListener('change', (event: Event) => {
            if (!(event.target instanceof HTMLSelectElement)) {
                return;
            }
            const format = event.target.value;
            event.target.value = 'Export';
            if (format === 'Export') {
                return;
            }
            void this.onDownloadClicked(format as '3DS' | 'DXF');
        });
        container.appendChild(exportDropDown);

        const pdfButton = this.createButton('pdf', 'icon_document.svg', translateLocalStorage('exportPdfDatasheet'), this.createPDF.bind(this));
        if (buildPdfTemplateUrl(this.session.baseUrl) == null) {
            pdfButton.disabled = true;
        }
        container.appendChild(pdfButton);

        this.updateTitle();
        window.addEventListener('resize', this.resizeListener);
        this.setCanvas();
    }

    /**
     * If we have multiple articles in the scene (i.e. by loading a .pec), then we need to show the user which one is currently selected by a bounding box.
     */
    public allowMainArticleSelection(value: boolean): void {
        if (this.coreApp.tools.defaultTool instanceof tool.SelectionDefault) {
            this.coreApp.tools.defaultTool.showMainElementSelection = value;
            this.coreApp.tools.defaultTool.deselectionEnabled = value;
        }
    }

    /**
     * Should by called after inserting new elements.
     */
    public resetCamera(): void {
        if (this.options.disableCameraPanning) {
            this.coreApp.viewer.view.cameraControl.setFixedTarget(this.getCenterOfSceneElement());
        }
        if (this.options.limitCameraDistanceByElementRadius) {
            const radiusMultiplier = 3;
            const maxZoomRadius: number = this.getRadiusOfSceneElement() * radiusMultiplier;
            this.coreApp.viewer.view.cameraControl.setNavigationArea(maxZoomRadius, this.getCenterOfSceneElement());
        }
        this.coreApp.viewer.view.cameraControl.setPosition(CameraControl.DEFAULT_CAMERA_POSITION);
        this.coreApp.viewer.view.cameraControl.zoomToFitElements(null);
        this.coreApp.viewer.requestRenderFrame();
        this.updateTitle();
        this.setCanvas();
    }

    public async ensureReady(context: string): Promise<boolean> {
        const layoutReady = await this.waitForViewerLayout();
        if (!layoutReady) {
            Log.warn(`ViewerUI::ensureReady layout not ready (${context})`);
            return false;
        }

        this.coreApp.viewer.requestRenderFrame();
        if (await this.waitForReflectionTextureReady(4000)) {
            return true;
        }

        Log.warn(`ViewerUI::ensureReady reflection texture stalled (${context}), retrying once`);
        return this.retryReflectionTextureLoad();
    }

    public updateTitle(): void {
        // Live Wagner prod currently renders no visible viewer title.
    }

    public zoomIn(): void {
        this.coreApp.viewer.view.cameraControl.zoomBy(constants.zoomInButtonValue);
    }

    public zoomOut(): void {
        this.coreApp.viewer.view.cameraControl.zoomBy(constants.zoomOutButtonValue);
    }

    public fullscreen(): void {
        const propertyEditor = HtmlUtils.getNotNullElementById('property-editor');
        propertyEditor.classList.toggle('invisible');
        this.setCanvas();
    }

    public async screenShot(): Promise<void> {
        const blob: Blob = await this.coreApp.viewer.createScreenshot({
            width: 800,
            height: 600,
            mimeType: 'image/jpeg'
        }, true);

        if (navigator.msSaveBlob != null) {
            navigator.msSaveBlob(blob, 'screenshot.jpg');
            return;
        }

        const link: HTMLAnchorElement = document.createElement('a');
        link.style.display = 'none';
        document.body.appendChild(link);

        const url: string = URL.createObjectURL(blob);
        link.href = url;
        link.download = 'screenshot.jpg';
        link.click();

        URL.revokeObjectURL(url);
        document.body.removeChild(link);
    }

    private getCenterOfSceneElement(): Vector3 {
        if (this.coreApp.model.elements.length > 0 && this.coreApp.model.elements[0].boundingBox.isValid()) {
            return this.coreApp.model.elements[0].boundingBox.getCenter();
        }
        return Vector3.Zero();
    }

    private getRadiusOfSceneElement(): number {
        if (this.coreApp.model.elements.length > 0 && this.coreApp.model.elements[0].boundingBox.isValid()) {
            return this.coreApp.model.elements[0].boundingBox.getRadius();
        }
        return 20;
    }

    private createButton(className: string, iconName: string, title: string, onClick: () => void | Promise<void>): HTMLButtonElement {
        const button: HTMLButtonElement = document.createElement('button');
        button.type = 'button';
        button.className = `pcon-button ${className}`;
        button.title = title;
        button.innerHTML = `<img src="${getAssetUrl(iconName)}" alt="" />`;
        button.onclick = () => {
            void onClick();
        };
        return button;
    }

    private setCanvas(): void {
        const appContainer = HtmlUtils.getNotNullElementById('app-container');
        const propertyEditor = HtmlUtils.getNotNullElementById('property-editor');
        const viewerContainer = HtmlUtils.getNotNullElementById('pcon-viewer');
        const viewerCanvas = viewerContainer.querySelector<HTMLCanvasElement>('canvas');

        const propertyEditorWidth = window.innerWidth < 821 || propertyEditor.classList.contains('invisible')
            ? 0
            : propertyEditor.offsetWidth;
        this.canvasWidth = Math.max(appContainer.offsetWidth - propertyEditorWidth, 0);

        if (window.innerWidth < 821) {
            viewerContainer.style.width = '100%';
        } else {
            viewerContainer.style.width = `${this.canvasWidth}px`;
        }

        if (viewerCanvas != null) {
            const resolvedWidth = window.innerWidth < 821 ? viewerContainer.offsetWidth : this.canvasWidth;
            viewerCanvas.style.width = `${resolvedWidth}px`;
            viewerCanvas.width = Math.max(Math.round(resolvedWidth * window.devicePixelRatio), resolvedWidth);
        }

        this.coreApp.viewer.resize(true);
        this.coreApp.viewer.requestRenderFrame();
    }

    private async waitForViewerLayout(): Promise<boolean> {
        const viewerContainer = HtmlUtils.getNotNullElementById('pcon-viewer') as HTMLDivElement;
        const size = await HtmlUtils.waitForElementSize(viewerContainer, 80, 240, 5000);
        if (size == null) {
            return false;
        }

        this.setCanvas();
        await HtmlUtils.nextAnimationFrame();
        await HtmlUtils.nextAnimationFrame();
        return size.width > 0 && size.height > 0;
    }

    private getTrackedReflectionTexture(): CubeTexture | null {
        const sceneMaterials = this.coreApp.scene.materials as Array<{ reflectionTexture?: CubeTexture | null }>;
        const sceneTexture = sceneMaterials
            .map((material) => material?.reflectionTexture ?? null)
            .find((texture): texture is CubeTexture => texture instanceof CubeTexture);

        return sceneTexture ?? this.coreApp.model.materials.getDefaultReflectionTexture();
    }

    private getReflectionTextureState(): { name: string | null; ready: boolean; width: number; height: number } {
        const reflectionTexture = this.getTrackedReflectionTexture();

        return {
            name: reflectionTexture?.name ?? null,
            ready: reflectionTexture?.isReady() ?? false,
            width: reflectionTexture?._texture?.width ?? 0,
            height: reflectionTexture?._texture?.height ?? 0,
        };
    }

    private async waitForReflectionTextureReady(timeoutMs: number): Promise<boolean> {
        const startedAt = Date.now();
        while ((Date.now() - startedAt) < timeoutMs) {
            const state = this.getReflectionTextureState();
            if (state.ready && state.width > 0 && state.height > 0) {
                return true;
            }

            this.coreApp.viewer.requestRenderFrame();
            await HtmlUtils.delay(100);
        }

        return false;
    }

    private async retryReflectionTextureLoad(): Promise<boolean> {
        const materialManager = this.coreApp.model.materials;
        const currentTextureName = this.getReflectionTextureState().name ?? buildAssetUrl('w-cf/data/core/textures/defaultHDR_512.env');
        const separator = currentTextureName.includes('?') ? '&' : '?';
        const retryUrl = `${currentTextureName}${separator}retry=${Date.now()}`;

        materialManager.customEnvironmentUrl = retryUrl;
        const replacementTexture = materialManager.getDefaultReflectionTexture();

        (materialManager.defaultMaterial as { reflectionTexture?: CubeTexture | null }).reflectionTexture = replacementTexture;
        (this.coreApp.scene.materials as Array<{ reflectionTexture?: CubeTexture | null }>).forEach((material) => {
            if (material?.reflectionTexture != null) {
                material.reflectionTexture = replacementTexture;
            }
        });

        this.setCanvas();
        return this.waitForReflectionTextureReady(6000);
    }

    private createExportDropdown(): HTMLSelectElement {
        const fileExportDropdown: HTMLSelectElement = document.createElement('select');
        ['Export', '3DS', 'DXF'].forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.text = value;
            fileExportDropdown.appendChild(option);
        });
        return fileExportDropdown;
    }

    private async createPDF(): Promise<void> {
        const pdfTemplateUrl = buildPdfTemplateUrl(this.session.baseUrl);
        if (pdfTemplateUrl == null) {
            window.alert(translateLocalStorage('pdfUnavailable'));
            return;
        }
        await this.onPdfDownloadClicked(pdfTemplateUrl);
    }

    private async onPdfDownloadClicked(pdfTemplateUrl: string): Promise<void> {
        const basketViewConfig = ((): basket.BasketViewConfig => {
            const config: basket.BasketViewConfig = new basket.BasketViewConfig();
            config.name = runtimeConfig.pdfViewName;
            config.viewId = runtimeConfig.pdfViewId;
            config.displayMode = runtimeConfig.pdfDisplayMode as basket.ViewDisplayMode;
            config.mergeMode = runtimeConfig.pdfMergeMode as basket.MergeMode;
            return config;
        })();

        try {
            const availableViews: Array<basket.BasketViewConfig> = await this.session.basket.getBasketViewConfigs();
            const compactViewExists = availableViews.some((value) => value.viewId === basketViewConfig.viewId);
            if (!compactViewExists) {
                await this.session.basket.addBasketView(basketViewConfig);
            }

            const itemIds: Array<string> = this.articleManager
                .getAllMainArticles()
                .map((article) => article.basketId)
                .filter((basketId): basketId is string => basketId != null);

            const pdfUrl: string | undefined = await ajax<string>(
                'POST',
                pdfTemplateUrl,
                JSON.stringify({
                    sessionId: this.session.sessionId,
                    viewId: basketViewConfig.viewId,
                    items: itemIds,
                    matchBasketItemIds: true
                }),
                { dataType: 'text' }
            );

            if (!isNullOrEmpty(pdfUrl)) {
                const link: HTMLAnchorElement = document.createElement('a');
                link.style.display = 'none';
                document.body.appendChild(link);
                link.href = pdfUrl;
                link.target = '_blank';
                link.download = 'report.pdf';
                link.click();
                document.body.removeChild(link);
                return;
            }
        } catch (error) {
            Log.error('Failed to export pdf.', error);
        }

        window.alert(translateLocalStorage('pdfUnavailable'));
    }

    private async onDownloadClicked(format: '3DS' | 'DXF' | 'DWG' | 'SKP'): Promise<void> {
        ProgressUI.beginLoading();
        let exportOptions: Array<string> | null = null;
        switch (format) {
            case 'DWG':
                exportOptions = [
                    'format=DWG',
                    'hideSubArticles=false',
                    'no2D=true',
                    'textures=true',
                    'materials=true'
                ];
                break;
            case 'DXF':
                exportOptions = [
                    'format=DWG',
                    'dxf=true',
                    'hideSubArticles=false',
                    'no2D=true',
                    'textures=true',
                    'materials=true'
                ];
                break;
            case '3DS':
                exportOptions = [
                    'format=3DS',
                    'hideSubArticles=false',
                    'textures=true'
                ];
                break;
            case 'SKP':
                exportOptions = [
                    'format=SKP',
                    'hideSubArticles=false',
                    'no2D=true',
                    'textures=false',
                    'textureToColor=true'
                ];
                break;
        }

        if (exportOptions == null) {
            ProgressUI.endLoading();
            return;
        }

        try {
            const articles: Array<cf.MainArticleElement> = this.articleManager.getAllMainArticles();
            const obxUrl: string = await this.articleManager.exportObx(articles);
            const folderId: string = await this.articleManager.session.basket.insertFolder(null, null, 'Set');
            const setId: string = await this.articleManager.session.basket.convertToSetArticle(folderId);
            const pastedIds: Array<string> = await this.articleManager.session.basket.paste(setId, null, obxUrl);

            if (pastedIds != null) {
                await this.articleManager.session.basket.addToSetArticle(pastedIds);
                const exportUrl: string = await this.articleManager.session.basket.getExportedGeometry(setId, exportOptions);
                const options: basket.DeleteItemsOptions = new basket.DeleteItemsOptions();
                options.subItems = true;
                await this.articleManager.session.basket.deleteItems([setId], options);
                await this.articleManager.session.deleteFile(obxUrl);

                if (!isNullOrEmpty(exportUrl)) {
                    window.location.href = exportUrl;
                }
            }
        } catch (error) {
            Log.error('Failed to export geometry.', error);
        } finally {
            ProgressUI.endLoading();
        }
    }
}
